import React, { useState, useEffect } from 'react'
import {
  Grid,
  Paper,
  Typography,
  Button,
  Alert,
  Box,
  Fab,
  Zoom
} from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import AHSelInlinePreview from '../AHSelInlinePreview'

interface OutputSheetsTabProps {
  showSheetPreview: boolean
  setShowSheetPreview: (show: boolean) => void
  currentProjectAHUs: any[]
  projectName: string
  ahuCalculationResults: any
  currentAHUId: string | null
  psychrometricData?: {
    points: any[],
    lines: any[],
    image?: string,  // 고해상도 PNG 이미지 (Base64)
    metadata?: any
  } | null
  currentProject?: any
}

const OutputSheetsTab: React.FC<OutputSheetsTabProps> = ({
  showSheetPreview,
  setShowSheetPreview,
  currentProjectAHUs,
  projectName,
  ahuCalculationResults,
  currentAHUId,
  psychrometricData,
  currentProject
}) => {
  // 급기량 검증: ahuCalculationResults에서 supplyFan.airflow 확인
  const supplyAirflow = ahuCalculationResults?.supplyFan?.airflow || 0;
  const isSupplyAirflowValid = supplyAirflow > 0;

  // 스크롤 위치 추적
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // 스크롤이 300px 이상 내려갔을 때 버튼 표시
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Grid container spacing={3}>
      {/* AH-Sel 시트 생성 버튼 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Output Sheets
          </Typography>

          {/* 급기량 검증 경고 메시지 */}
          {!isSupplyAirflowValid && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                ⚠️ 출력 시트를 생성하려면 '풍량 선정' 탭에서 급기량을 먼저 계산해야 합니다.
              </Typography>
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={!isSupplyAirflowValid}
            onClick={() => {
              // AH Selection Sheet 생성 및 미리보기 표시
              setShowSheetPreview(true);
            }}
          >
            AH Selection Sheet 생성
          </Button>

          {/* 급기량 표시 (디버그용) */}
          {isSupplyAirflowValid && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                현재 급기량: {Math.round(supplyAirflow).toLocaleString()} m³/h
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* AH-Sel 시트 미리보기 */}
      {showSheetPreview && (
        <Grid item xs={12}>
          <AHSelInlinePreview
            ahuData={currentProjectAHUs}
            projectName={projectName}
            ahuCalculationResults={ahuCalculationResults}
            currentProjectAHUs={currentProjectAHUs}
            currentAHUId={currentAHUId}
            psychrometricData={psychrometricData}
            currentProject={currentProject}
          />
        </Grid>
      )}

      {/* 맨 위로 가기 버튼 */}
      <Zoom in={showScrollTop}>
        <Fab
          color="primary"
          size="small"
          aria-label="scroll back to top"
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Grid>
  )
}

export default OutputSheetsTab